import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, SparklesIcon } from 'lucide-react';
interface ExecutiveSummaryProps {
  open: boolean;
  onClose: () => void;
}
export function ExecutiveSummary({
  open,
  onClose
}: ExecutiveSummaryProps) {
  return <AnimatePresence data-id="element-20">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 md:p-8" onClick={onClose} data-id="element-21">
          <motion.div initial={{
        scale: 0.96,
        opacity: 0,
        y: 20
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} exit={{
        scale: 0.96,
        opacity: 0,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-22">
            {/* Header */}
            <div className="flex items-center justify-between px-6 md:px-10 py-5 border-b border-slate-200 bg-white sticky top-0 z-10" data-id="element-23">
              <div className="flex items-center gap-3" data-id="element-24">
                <div className="w-10 h-10 rounded-xl bg-[#6C2BD9] flex items-center justify-center" data-id="element-25">
                  <SparklesIcon className="w-5 h-5 text-white" data-id="element-26" />
                </div>
                <div data-id="element-27">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[#6C2BD9] font-semibold" data-id="element-28">
                    Snakker · Customer Overview
                  </div>
                  <h2 className="text-lg md:text-xl font-bold text-slate-900 leading-tight" data-id="element-29">
                    Executive Summary
                  </h2>
                </div>
              </div>
              <button onClick={onClose} className="w-10 h-10 rounded-full hover:bg-slate-100 flex items-center justify-center transition-colors" aria-label="Close" data-id="element-30">
                <XIcon className="w-5 h-5 text-slate-600" data-id="element-31" />
              </button>
            </div>

            {/* Written summary — written for Brand & Portfolio Managers */}
            <article className="overflow-y-auto px-6 md:px-12 py-10 prose prose-slate max-w-none" data-id="element-32">
              <p className="text-xl md:text-2xl font-semibold text-slate-900 leading-snug mb-8" data-id="element-33">
                Snakker is a partner for{' '}
                <span className="text-[#6C2BD9]" data-id="element-34">
                  Brand & Portfolio Managers
                </span>{' '}
                — helping you get more value out of every brand you own, with
                less manual work and a fraction of the campaign cost you're used
                to.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-35">
                Written for You
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-36">
                You manage brands that are still meaningful to patients,
                prescribers and the P&L — but their marketing budgets are
                shrinking and the agency model that worked at launch doesn't fit
                anymore. Snakker is built around that reality. We give you a way
                to keep your brands visible, relevant and growing without
                expanding your team, your hours, or your cost base.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-37">
                More Potential From Every Brand
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-38">
                Mature brands quietly carry a huge share of portfolio revenue,
                yet they're almost always under-supported. Snakker helps you
                change that. By running continuous, lightweight campaigns across
                HCP audiences, we <strong data-id="element-39">defend market share</strong>, surface{' '}
                <strong data-id="element-40">new growth pockets</strong> across countries, and{' '}
                <strong data-id="element-41">extend the productive lifespan</strong> of brands that
                would otherwise quietly fade. Even small improvements compound
                across a portfolio — meaning bigger numbers next to your name,
                not someone else's.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-42">
                Three Levers That Compound
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-43">
                Our approach pulls three levers that compound on each other. We{' '}
                <strong data-id="element-44">reduce erosion</strong> through continuous, data-driven
                engagement that keeps HCPs connected to the brand. We{' '}
                <strong data-id="element-45">increase profitability</strong> by replacing traditional
                marketing with AI-automated execution, widening the margin on
                every euro of revenue. And we <strong data-id="element-46">prolong lifespan</strong>{' '}
                — lower costs and slower erosion keep the gap between revenue
                and cost positive for longer.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-47">
                Less Work, Less Coordination
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-48">
                You shouldn't have to chase agencies, MLR reviewers, country
                affiliates and creative vendors for every small campaign.
                Snakker takes the operational weight off your desk:
                storyboarding, asset adaptation, localization and distribution
                are handled in days, not months. You stay focused on{' '}
                <strong data-id="element-49">
                  strategy, prioritization and stakeholder conversations
                </strong>{' '}
                — the parts of your job that actually require your judgement.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-50">
                Dramatically Lower Cost per Localized Campaign
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-51">
                Traditional pharma campaigns are too expensive to justify on
                mature revenue. Snakker rebuilds the unit economics from the
                ground up. A fully managed, localized campaign typically lands
                in the <strong data-id="element-52">low-thousands of euros</strong>, not six-figures.
                And the more you scale, the cheaper it gets: volume benefits
                kick in across <strong data-id="element-53">Master Snak</strong> (drops from €2,900 →
                €1,900 as campaigns scale),{' '}
                <strong data-id="element-54">country localization</strong> (€999 → €500 as countries
                scale 2 → 50+), and <strong data-id="element-55">data insights & hosting</strong>{' '}
                (€99/mo → €49/mo on the same country curve).
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-56">
                What Makes Us Uniquely Able To Do This
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-57">
                Snakker combines{' '}
                <strong data-id="element-58">
                  three decades of pharma sales & marketing expertise
                </strong>{' '}
                with a focused AI engine — 100% on healthcare, nothing else. We
                hold{' '}
                <strong data-id="element-59">
                  master agreements with 15 of the top 30 healthcare companies
                  globally
                </strong>
                , so procurement is already cleared. We're{' '}
                <strong data-id="element-60">content agnostic</strong> (work from your approved
                assets), <strong data-id="element-61">publishing agnostic</strong> (email, banners,
                WhatsApp, SMS, Veeva, portals, sales reps, congresses), and ship
                an <strong data-id="element-62">actionable data layer</strong> that streams into any
                analytics platform (Veeva, Tableau, Power BI, CDP) or stands
                alone.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-63">
                You Stay in Control
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-64">
                Snakker is not here to replace you — and not here to take over
                your brand.{' '}
                <strong data-id="element-65">
                  You own the strategy, the messaging priorities, and the final
                  sign-off.
                </strong>{' '}
                We work from your approved materials and within your compliance
                guardrails. Think of Snakker as the execution engine sitting
                behind your decisions: it makes you faster, broader, and more
                measurable — while the brand, the relationships and the credit
                stay with you.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-66">
                How It Works in Practice
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-67">
                We start from <strong data-id="element-68">your existing approved content</strong> —
                no new asset creation needed. We build one{' '}
                <strong data-id="element-69">Master Snak</strong> as the parent presentation, then
                localize it per country for language and regulatory
                requirements. We publish across the right channels and feed
                every interaction back as a continuous flywheel: Launch → Engage
                → Measure → Learn → Optimize → Relaunch. Bi-monthly, you receive
                not only real-world engagement data but{' '}
                <strong data-id="element-70">actionable suggestions for improvement</strong>. Every
                campaign makes the next one sharper.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-71">
                What You Get
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-72">
                A practical, repeatable way to{' '}
                <strong data-id="element-73">do more with your portfolio</strong>: more brands
                supported, more countries activated, more campaigns per year,
                clearer insight into what's working — at a cost structure that
                finally fits the revenue you're working with. And a calmer week,
                because the heavy lifting of campaign execution stops landing on
                your team.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-74">
                In One Line
              </h3>
              <p className="text-lg font-semibold text-slate-900 leading-snug mt-2 pb-4" data-id="element-75">
                Snakker helps Brand & Portfolio Managers unlock more value from
                every brand they own — with less workload, lower cost, and full
                control left exactly where it belongs: with you.
              </p>
            </article>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}